import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';

/**
 * Config leída de variables de entorno. Nada de claves en el repo:
 * ver .env.example.
 */
function requireEnv(key: string): string {
  const value = import.meta.env[key as keyof ImportMetaEnv];
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `Falta la variable de entorno ${key}. Copiá .env.example a .env.local y completala.`,
    );
  }
  return value;
}

const firebaseConfig = {
  apiKey: requireEnv('VITE_FIREBASE_API_KEY'),
  authDomain: requireEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: requireEnv('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: requireEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: requireEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: requireEnv('VITE_FIREBASE_APP_ID'),
};

export const app: FirebaseApp = initializeApp(firebaseConfig);

/**
 * Auth sin el resolvedor de ventanas emergentes.
 *
 * `getAuth()` registra ese resolvedor solo, y eso hace que TODA visita cargue
 * un iframe desde `<proyecto>.firebaseapp.com`: 92 KB de JavaScript y 125 ms de
 * procesador, medidos por PageSpeed, en la portada de alguien que ni siquiera
 * está intentando entrar.
 *
 * Con `initializeAuth` el resolvedor no se registra, y se lo pasa a mano en el
 * único lugar donde hace falta: `signInWithPopup`, en services/auth.ts. Ahí el
 * iframe se carga recién cuando alguien aprieta "Continuar con Google", que es
 * cuando efectivamente se necesita.
 *
 * El orden de persistencia es el que usa `getAuth` por defecto: IndexedDB
 * primero, y localStorage donde no esté disponible (algunos navegadores en modo
 * privado). Sin la lista explícita, la sesión no sobreviviría a recargar.
 */
export const auth: Auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence],
});
export const db: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
export const functions: Functions = getFunctions(
  app,
  import.meta.env.VITE_FUNCTIONS_REGION || 'us-central1',
);

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

export const siteUrl: string = import.meta.env.VITE_SITE_URL || window.location.origin;
